// API service for the Listening Master contests
import { API_URL } from '../app/constants'

// Add caching mechanism
const apiCache = {
  groups: null,
  groupChallenges: {},
  cacheTTL: 5 * 60 * 1000 // 5 minutes cache expiration
};

// Function to clear all cache data - useful when user level changes
export const clearListeningCache = () => {
  console.log("Clearing listening challenge cache");
  apiCache.groups = null;
  apiCache.groupChallenges = {};
};

// Add throttling and retry mechanisms
let lastRequestTime = 0;
let isFirstRequest = true; // Track first request to skip throttling
const MIN_REQUEST_INTERVAL = 300; // ms between requests

// Throttled fetch to prevent rate limiting
const throttledFetch = async (url, options = {}) => {
  const now = Date.now();
  const timeSinceLastRequest = now - lastRequestTime;
  
  // Skip throttling for the first request to make initial page load faster
  if (!isFirstRequest && timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
    // Wait to prevent hitting rate limits
    await new Promise(resolve => setTimeout(resolve, MIN_REQUEST_INTERVAL - timeSinceLastRequest));
  }
  
  // Mark first request as completed
  if (isFirstRequest) {
    isFirstRequest = false;
  }
  
  // Update last request time
  lastRequestTime = Date.now();
  
  // Get fresh JWT token
  const jwt = localStorage.getItem('jwt');
  if (!jwt) {
    throw new Error('Authentication token missing');
  }
  
  // Debug: Log JWT payload to see user info (only once)
  if (!window.jwtLogged) {
    try {
      const jwtPayload = JSON.parse(atob(jwt.split('.')[1]));
      console.log('JWT payload:', jwtPayload);
      window.jwtLogged = true;
    } catch (e) {
      console.log('Could not decode JWT for debugging');
    }
  }
  
  // Merge headers with defaults
  const headers = {
    'Authorization': `Bearer ${jwt}`,
    'Content-Type': 'application/json',
    ...options.headers
  };
  
  // Execute the fetch with retry logic
  let attempts = 0;
  const maxAttempts = 3;
  
  while (attempts < maxAttempts) {
    try {
      console.log(`API Request (attempt ${attempts+1}/${maxAttempts}): ${options.method || 'GET'} ${url}`);
      const response = await fetch(url, {...options, headers});
      
      if (response.status === 429) {
        // Rate limited - handle with exponential backoff
        attempts++;
        const retryAfter = response.headers.get('Retry-After') || Math.pow(2, attempts);
        console.warn(`Rate limited (429). Retrying after ${retryAfter}s...`);
        await new Promise(resolve => setTimeout(resolve, retryAfter * 1000));
        continue;
      }
      
      return response;
    } catch (error) {
      attempts++;
      if (attempts >= maxAttempts) throw error;
      
      // Exponential backoff
      const backoffTime = Math.pow(2, attempts) * 500;
      console.warn(`Request failed. Retrying in ${backoffTime}ms...`);
      await new Promise(resolve => setTimeout(resolve, backoffTime));
    }
  }
};

// Get all listening challenge groups
export const getListeningGroups = async () => {
  try {
    // Check cache but with shorter TTL for level-sensitive data
    const now = Date.now();
    if (apiCache.groups && apiCache.groups.timestamp && 
        (now - apiCache.groups.timestamp < 30000)) { // 30 seconds cache
      console.log("Using cached listening groups data");
      return apiCache.groups.data;
    }
    
    // Backend will get user level from JWT token directly
    const url = `${API_URL}/api/v1/listening/groups`;
    
    // Fetch fresh data, use the throttled fetch with built-in retry
    const response = await throttledFetch(url);
    
    if (!response.ok) {
      throw new Error('Failed to fetch listening challenge groups');
    }
    
    const data = await response.json();
    console.log("API returned challenge groups:", data);
    
    // Cache the response with a timestamp
    apiCache.groups = {
      data,
      timestamp: Date.now()
    };
    
    return data;
  } catch (error) {
    console.error("Error fetching listening challenge groups:", error);
    throw error;
  }
};

// Get challenges for a specific group
export const getGroupChallenges = async (groupId) => {
  try {
    // Check cache but with shorter TTL for level-sensitive data
    const now = Date.now();
    if (apiCache.groupChallenges[groupId] && apiCache.groupChallenges[groupId].timestamp && 
        (now - apiCache.groupChallenges[groupId].timestamp < 30000)) { // 30 seconds cache
      console.log(`Using cached challenges for group ${groupId}`);
      return apiCache.groupChallenges[groupId].data;
    }

    // Backend will get user level from JWT token directly
    const url = `${API_URL}/api/v1/listening/groups/${groupId}/challenges`;

    // Fetch fresh data, use the throttled fetch with built-in retry
    const response = await throttledFetch(url);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch challenges for group ${groupId}`);
    }
    
    const data = await response.json();
    console.log(`API returned challenges for group ${groupId}:`, data);
    
    // Cache the response with a timestamp
    apiCache.groupChallenges[groupId] = {
      data,
      timestamp: Date.now()
    };
    
    return data;
  } catch (error) {
    console.error(`Error fetching challenges for group ${groupId}:`, error);
    throw error;
  }
};

// Get all listening challenges (individual)
export const getListeningChallenges = async () => {
  try {
    // Backend will get user level from JWT token directly
    const url = `${API_URL}/api/v1/listening/challenges`;
    
    // Use the throttled fetch with built-in retry
    const response = await throttledFetch(url);
    
    if (!response.ok) {
      throw new Error('Failed to fetch listening challenges');
    }
    
    const data = await response.json();
    console.log("API returned challenges:", data);
    
    // Keep the original data structure for compatibility
    return data;
  } catch (error) {
    console.error("Error fetching listening challenges:", error);
    throw error;
  }
};

// Get a specific listening challenge by ID
export const getListeningChallenge = async (challengeId) => {
  try {
    const response = await throttledFetch(`${API_URL}/api/v1/listening/challenges/${challengeId}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch listening challenge');
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Error fetching listening challenge ${challengeId}:`, error);
    throw error;
  }
};

// Mark a challenge as completed
export const completeListeningChallenge = async (challengeId) => {
  try {
    const response = await throttledFetch(`${API_URL}/api/v1/listening/challenges/${challengeId}/complete`, {
      method: 'POST'
    });
    
    if (!response.ok) {
      throw new Error('Failed to mark challenge as completed');
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Error completing listening challenge ${challengeId}:`, error);
    throw error;
  }
};

// Stream chat for a listening challenge - using genai endpoint
export const getListeningChatStream = async (challengeId, message, youtubeUrl, conversationHistory = []) => {
  try {
    console.log(`Sending chat request for challenge ID: ${challengeId}, YouTube URL: ${youtubeUrl}, message: "${message}"`);
    const jwt = localStorage.getItem('jwt');
    const response = await fetch(`${API_URL}/api/v1/genai/ask_question_listening_challenge`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${jwt}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        youtube_url: youtubeUrl,
        user_input: message,
        conversation_history: conversationHistory
      })
    });
    
    if (!response.ok) {
      console.error(`Chat API response error: ${response.status} - ${response.statusText}`);
      throw new Error('Failed to get chat response');
    }
    
    console.log('Chat stream response received successfully');
    return response;
  } catch (error) {
    console.error(`Error getting chat response for challenge ${challengeId}:`, error);
    throw error;
  }
};

// Validate a transcript for a listening challenge
export const validateListeningTranscript = async (challengeId, userScript) => {
  try {
    console.log(`Validating transcript for challenge ID: ${challengeId}`);
    const jwt = localStorage.getItem('jwt');
    const response = await fetch(`${API_URL}/api/v1/listening/challenges/${challengeId}/validate`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${jwt}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userScript
      })
    });
    
    if (!response.ok) {
      console.error(`Validation API response error: ${response.status} - ${response.statusText}`);
      throw new Error('Failed to validate transcript');
    }
    
    const result = await response.json();
    console.log('Validation result:', result);
    return result;
  } catch (error) {
    console.error(`Error validating transcript for challenge ${challengeId}:`, error);
    throw error;
  }
};

// Admin functions

// Create a new listening challenge
export const createListeningChallenge = async (challengeData) => {
  try {
    const jwt = localStorage.getItem('jwt');
    const response = await fetch(`${API_URL}/api/v1/listening/admin/challenges`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${jwt}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(challengeData)
    });
    
    if (!response.ok) {
      throw new Error('Failed to create listening challenge');
    }
    
    return await response.json();
  } catch (error) {
    console.error("Error creating listening challenge:", error);
    throw error;
  }
};

// Update an existing listening challenge
export const updateListeningChallenge = async (challengeId, challengeData) => {
  try {
    const jwt = localStorage.getItem('jwt');
    const response = await fetch(`${API_URL}/api/v1/listening/admin/challenges/${challengeId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${jwt}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(challengeData)
    });
    
    if (!response.ok) {
      throw new Error('Failed to update listening challenge');
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Error updating listening challenge ${challengeId}:`, error);
    throw error;
  }
};

// Delete a listening challenge
export const deleteListeningChallenge = async (challengeId) => {
  try {
    const jwt = localStorage.getItem('jwt');
    const response = await fetch(`${API_URL}/api/v1/listening/admin/challenges/${challengeId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${jwt}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to delete listening challenge');
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Error deleting listening challenge ${challengeId}:`, error);
    throw error;
  }
};